import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  MessageCircle, Send, RefreshCw, Search, ChevronLeft,
  Clock, CheckCheck, Check, XCircle, Loader2, PenSquare,
  User, Phone, ArrowLeft, Paperclip, Image as ImageIcon, FileText, Smile,
  Bot, Pause, Play, Edit2, Trash2, BarChart3, ChevronDown, Copy as CopyIcon,
  MoreVertical, X, StickyNote, Plus, CornerUpLeft, Forward, MousePointerClick,
  Film, UserPlus, ClipboardList,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import MessageRenderer, { QuotedMessage } from "./messages";
import TemplateSendModal from "./TemplateSendModal";
import InteractiveComposerModal from "./InteractiveComposerModal";
import { AppModal, ConfirmModal } from "../ui";
import WhatsAppSendingStatus from "./WhatsAppSendingStatus";
import ConvertToEnquiryModal from "./ConvertToEnquiryModal";
import "./whatsapp-chat-inbox.scss";

/* ── Message pagination tuning ────────────────────────────────────────────── */
/* Matches the server's default page size. Page 1 is always the NEWEST slice —
   a chat opens at the bottom — and scrolling up pulls progressively older
   pages, which are prepended to the list. */
const MESSAGE_PAGE_SIZE = 50;
/* Distance from the top that triggers loading the next older page. */
const LOAD_OLDER_AT_PX = 80;
/* Within this distance of the bottom, incoming messages auto-scroll. Further
   up than this and the user is reading history, so we leave them alone. */
const NEAR_BOTTOM_PX = 120;

/* ── Conversation-list pagination tuning ──────────────────────────────────── */
/* The chat list is paged the same way the message list is: page 1 is the most
   recently active slice and higher pages are progressively older, appended as
   the admin scrolls down. Without this the sidebar showed one server page and
   every earlier contact was unreachable — the list looked complete because a
   chat list has no natural end. */
const CONVERSATION_PAGE_SIZE = 30;
/* Distance from the bottom of the chat list that pulls the next page. */
const LOAD_MORE_CONVS_AT_PX = 120;
/* Keystrokes settle for this long before the search hits the server. */
const SEARCH_DEBOUNCE_MS = 300;

/* ── Swipe-to-reply tuning ────────────────────────────────────────────────── */
/* Release past this and the message gets quoted. */
const SWIPE_TRIGGER_PX = 56;
/* Rubber-band ceiling — the bubble stops travelling here however far the finger
   goes, so a long drag doesn't shove it off screen. */
const SWIPE_MAX_PX = 78;
/* Horizontal travel required before the gesture is treated as a swipe rather
   than a scroll. Below this the list keeps the touch. */
const SWIPE_LOCK_PX = 12;

/* ── Helpers ──────────────────────────────────────────────────────────────── */
/* Accepts either the paginated shape ({ messages, hasMore }) or a bare array,
   so a caller that doesn't paginate keeps working. */
const normalizeMessagePage = (res) => {
  if (Array.isArray(res)) return { list: res, hasMore: false };
  return {
    list: Array.isArray(res?.messages) ? res.messages : [],
    hasMore: !!res?.hasMore,
  };
};

/* Accepts either the paginated shape ({ conversations, hasMore }) or a bare
   array. `paginated` is what tells the list whether the SERVER answered the
   search: when it did, filtering again in the browser could only remove rows
   the server matched more cleverly than we can (a phone typed with a country
   code and spaces, say). */
const normalizeConversationPage = (res) => {
  if (Array.isArray(res)) return { list: res, hasMore: false, paginated: false };
  return {
    list: Array.isArray(res?.conversations) ? res.conversations : [],
    hasMore: !!res?.hasMore,
    paginated: true,
  };
};

/* Merge two conversation slices into one list, de-duplicated by _id and ordered
   by most recent activity — the same order the server sorts by.

   Merging rather than replacing is what lets a refresh (or a socket ping) pull
   the newest page in without discarding the older pages the admin scrolled
   back through. `incoming` wins on conflict, so a new last message, preview or
   unread count lands. */
const mergeConversations = (existing, incoming) => {
  const byId = new Map();
  for (const c of existing) if (c?._id) byId.set(String(c._id), c);
  for (const c of incoming) if (c?._id) byId.set(String(c._id), c);
  return [...byId.values()]
    .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
};

/* Merge two message slices into one chronological list, de-duplicated by _id.
   `incoming` wins on conflict so a refresh picks up new delivery/read ticks and
   soft-delete tombstones. Merging (rather than replacing) is what lets older
   pages the user scrolled back through survive a refresh. */
const mergeMessages = (existing, incoming) => {
  const byId = new Map();
  const keyless = [];
  const add = (m) => {
    if (!m) return;
    if (m._id) byId.set(String(m._id), m);
    else keyless.push(m);
  };
  existing.forEach(add);
  incoming.forEach(add);
  return [...byId.values(), ...keyless]
    .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
};

const statusIcon = (s) => {
  if (s === "read") return <CheckCheck size={13} className="wai-tick--read" />;
  if (s === "delivered") return <CheckCheck size={13} className="wai-tick--delivered" />;
  if (s === "sent") return <Check size={13} className="wai-tick--sent" />;
  if (s === "failed") return <XCircle size={13} className="wai-tick--failed" />;
  return <Clock size={13} className="wai-tick--pending" />;
};

const formatChatTime = (d) => {
  if (!d) return "";
  const date = new Date(d);
  const now = new Date();
  const diff = now - date;
  const oneDay = 86400000;
  if (diff < oneDay && now.getDate() === date.getDate())
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (diff < 2 * oneDay) return "Yesterday";
  return date.toLocaleDateString([], { day: "2-digit", month: "2-digit", year: "2-digit" });
};

const formatNoteTime = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleString([], {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const formatDateSeparator = (d) => {
  if (!d) return "";
  const date = new Date(d);
  const now = new Date();
  const diff = Math.floor((now - date) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return date.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
};

const groupByDate = (messages) => {
  const groups = [];
  let currentLabel = "";
  for (const m of messages) {
    const label = formatDateSeparator(m.createdAt);
    if (label !== currentLabel) {
      currentLabel = label;
      groups.push({ type: "date", label });
    }
    groups.push({ type: "msg", data: m });
  }
  return groups;
};

/* WhatsApp picks the message type from the MIME type, not the file extension —
   the upload endpoint echoes back the type it stored, which is what we trust. */
const resolveMessageType = (mime = "") =>
  mime.startsWith("image/") ? "image"
    : mime.startsWith("video/") ? "video"
      : mime.startsWith("audio/") ? "audio" : "document";

const isPreviewable = (mime = "") => /^(image|video)\//.test(mime);

const formatBytes = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
};

const ChatAvatar = ({ name, phone }) => {
  const initials = name
    ? name.split(" ").filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase()
    : (phone || "?").slice(-2);
  return <div className="wai-avatar">{initials}</div>;
};

const Loader = () => (
  <div className="wai-loader"><Loader2 size={20} className="wai-spin" /> Loading…</div>
);

/* Auto-growing message composer — multi-line like the WhatsApp app.
   Enter sends; Shift+Enter inserts a newline. Grows up to ~6 lines then scrolls. */
/* `inputRef` is an optional second ref for callers that need to focus the field
   (starting a quoted reply does). The auto-grow measurement keeps its own ref
   because it runs on every value change and must never be null. */
const AutoGrowTextarea = ({ value, className, inputRef, ...rest }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [value]);
  return (
    <textarea
      ref={(el) => {
        ref.current = el;
        if (inputRef) inputRef.current = el;
      }}
      rows={1}
      className={className}
      value={value}
      {...rest}
    />
  );
};

/**
 * WhatsAppChatInbox — Shared WhatsApp Web–style inbox component.
 *
 * Props:
 *   fetchConversations(statusFilter, { page, limit, search })
 *       → Promise<{ conversations[], hasMore }>
 *     Page 1 is the most recently active slice; higher pages are progressively
 *     older and are appended as the admin scrolls the chat list. `search` is
 *     answered by the SERVER — filtering in the browser can only ever search
 *     the page it happens to hold, which is how contacts older than one page
 *     became unfindable. Returning a bare array is still supported and is
 *     treated as the complete, unpaginated list (the browser then filters it).
 *     Each item: { _id, name, phone, preview, updatedAt, unreadCount, status, studentName? }
 *   fetchMessages(conversationId, { page, limit }) → Promise<{ messages[], hasMore }>
 *     `messages` is oldest → newest *within the page*; page 1 is the NEWEST
 *     slice and higher pages are progressively older (scroll-up history).
 *     `hasMore` says whether an older page exists. Returning a bare array is
 *     still supported and is treated as the complete, unpaginated history.
 *     Each message: { _id, direction, content, status, createdAt }
 *   sendReply(conversation, text) → Promise
 *   updateStatus(conversationId, status) → Promise
 *   showToast(msg, type)
 *   socket              — Socket.IO instance (from useSocket()). Listens for 'whatsapp:newMessage'.
 *   fetchSendingHealth(force) → Promise<payload>   optional; see WhatsAppSendingStatus
 */
const WhatsAppChatInbox = ({
  fetchConversations,
  fetchMessages,
  sendReply,
  updateStatus,
  showToast,
  socket,
  // Backend paths used to stream/upload media. Defaults match the institute API;
  // the platform inbox passes "/platform/whatsapp/..." instead.
  mediaEndpoint = "/whatsapp/media",
  uploadEndpoint = "/whatsapp/upload-media",
  // Optional: soft-delete a message from the inbox (local only — does NOT recall
  // it from the recipient's WhatsApp). Receives the message; should return a
  // resolved promise on success. When omitted, the delete menu is hidden.
  deleteMessage,
  // Optional: called after a conversation is opened (and thus marked read on the
  // server) so the caller can re-sync the sidebar unread badge.
  onConversationRead,
  // Optional: private enquiry notes on a contact (platform/SaaS-admin inbox only).
  // When fetchNotes is provided, a Notes section is shown in the contact-info
  // drawer. These notes are internal and never sent over WhatsApp.
  //   fetchNotes(conversationId) → Promise<notes[]>   (each: { _id, body, createdByName, createdAt, updatedAt })
  //   addNote(conversationId, body) → Promise<note>
  //   editNote(noteId, body) → Promise<note>
  //   deleteNote(noteId) → Promise
  fetchNotes,
  addNote,
  editNote,
  deleteNote,
  // Optional: enable WhatsApp template sending. When fetchTemplates is provided,
  // a "Template" affordance is shown in the reply bar (to re-open an expired 24h
  // window) and the New Message composer (first contact to a new number).
  //   fetchTemplates() → Promise<template[]>   (each: { name, language, category, status, components })
  fetchTemplates,
  // Optional: send a brand-new message (compose panel). Receives a payload of
  // { messageType, text?|templateName/languageCode/components, toPhone?|studentId? }.
  // Defaults to the institute /whatsapp/send-message endpoint; the platform inbox
  // passes one that targets /platform/whatsapp/send.
  sendNewMessage,
  // Whether the composer can search the student directory. The platform inbox has
  // no students, so it disables this and accepts phone numbers only.
  composeAllowStudentSearch = true,
  // Whether a template variable can be mapped to an LMS field instead of typed.
  // Requires the send endpoints behind sendReply/sendNewMessage to understand
  // `paramConfig`; the platform inbox's do not, and it has no institute context
  // to resolve against either, so it turns this off.
  templateVarsEnabled = true,
  // Optional: send an interactive message whose buttons carry server-side
  // actions. Both must be supplied together; when either is missing the
  // affordance is hidden. The platform inbox has no action engine behind it, so
  // it simply doesn't pass them.
  //   fetchButtonActions() → Promise<action[]>
  //   sendInteractiveButtons(conversation, { body, header, footer, buttons }) → Promise
  fetchButtonActions,
  sendInteractiveButtons,
  // Optional: turn a chat into an enquiry on the chat's own number. Supplied
  // only by the institute inbox, and only when the user may add enquiries; the
  // platform inbox has no enquiries. When `openEnquiry` is supplied too, a chat
  // already linked to an enquiry offers to open it.
  //   convertToEnquiry(conversation, { fullName, email, courseId, existing? }) → Promise<{ enquiry, linked? }>
  //   loadEnquiryCourses() → Promise<course[]>
  //   openEnquiry(enquiryId)
  convertToEnquiry,
  loadEnquiryCourses,
  openEnquiry,
  // Optional: live Meta verdict on whether this number can deliver an outbound
  // message. When supplied, a status strip sits above the chat list — quiet
  // when the account is healthy, tinted and one press from the fix when it is
  // not. Omitted, nothing is rendered: a send that returns 200 and never
  // arrives is exactly the state this closes, and guessing at it would be worse
  // than saying nothing.
  //   fetchSendingHealth(force) → Promise<payload>
  fetchSendingHealth,
  // Optional: false leaves the inbox read-only — no reply bar, new message,
  // status change, AI pause / override or AI drafts.
  canReply = true,
}) => {
  const notesEnabled = typeof fetchNotes === "function";
  const templatesEnabled = typeof fetchTemplates === "function";
  const interactiveEnabled =
    typeof fetchButtonActions === "function" && typeof sendInteractiveButtons === "function";
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const messagesEndRef = useRef(null);

  /* ── Chat-list pagination ────────────────────────────────────────────────
     `searchTerm` is what the box shows; `debouncedSearch` is what the server is
     asked for, so a five-letter name is one query rather than five. The refs
     mirror the three values the scroll handler and the socket refresh read —
     both fire outside React's render cycle and would otherwise close over a
     stale filter and quietly page through the wrong list. */
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [hasMoreConvs, setHasMoreConvs] = useState(false);
  const [loadingMoreConvs, setLoadingMoreConvs] = useState(false);
  /* False only for a caller that still returns a bare array; see
     normalizeConversationPage. */
  const [serverPaged, setServerPaged] = useState(true);
  const convScrollRef = useRef(null);
  const convPageRef = useRef(1);
  const hasMoreConvsRef = useRef(false);
  const loadingMoreConvsRef = useRef(false);
  const statusFilterRef = useRef("");
  statusFilterRef.current = statusFilter;
  const searchRef = useRef("");
  searchRef.current = debouncedSearch;

  /* Kept in a ref so async continuations can tell whether the user switched
     conversations while a fetch was in flight. Assigned during render. */
  const selectedRef = useRef(null);
  selectedRef.current = selected;

  /* ── Scroll-up history pagination ────────────────────────────────────────
     `loadingOlder` / `hasMoreOlder` drive the UI; the mirrored refs are read
     inside the scroll handler, which fires far too often to want a re-created
     callback (and would otherwise close over stale state). */
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  const messagesScrollRef = useRef(null);   // the .wai-messages scroll container
  const msgPageRef = useRef(1);             // highest page loaded (1 = newest)
  const loadingOlderRef = useRef(false);
  const hasMoreOlderRef = useRef(false);
  const nearBottomRef = useRef(true);
  /* Set just before older messages are prepended: { prevHeight, prevTop }.
     Consumed by the layout effect to hold the viewport steady. */
  const restoreScrollRef = useRef(null);
  /* Scroll intent for the next messages change: "auto" | "smooth" | null.
     null means "don't move" — e.g. after prepending history. */
  const autoScrollRef = useRef(null);
  /* AI agent state */
  const [aiDrafts, setAiDrafts] = useState([]);
  const [aiDraftEdit, setAiDraftEdit] = useState({}); // { [draftId]: editedText }
  const [aiDraftBusy, setAiDraftBusy] = useState({}); // { [draftId]: 'sending'|'discarding' }
  const [aiPauseBusy, setAiPauseBusy] = useState(false);

  /* Contact details drawer (WhatsApp-style "contact info"). On mobile this is
     where AI + status controls live, keeping the header uncluttered. */
  const [showDetails, setShowDetails] = useState(false);

  /* Private enquiry notes (platform inbox only — gated by notesEnabled). */
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [noteBusyId, setNoteBusyId] = useState(null); // id being saved/deleted
  const [confirmDeleteNote, setConfirmDeleteNote] = useState(null);

  /* Per-message options menu — rendered in a portal so it can't be clipped by
     the scrollable messages area, and flips upward near the viewport bottom. */
  const [menuMsg, setMenuMsg] = useState(null);       // message whose menu is open
  const [menuPos, setMenuPos] = useState(null);       // { rightOffset, top?, bottom? }
  const [confirmDeleteMsg, setConfirmDeleteMsg] = useState(null); // message pending delete
  const [deletingMsg, setDeletingMsg] = useState(false);

  /* ── Quoted reply ────────────────────────────────────────────────────────
     `replyingTo` is the message the composer is currently quoting (null when
     composing normally). Only its _id crosses the wire — the server re-reads
     the message and re-derives the preview, so a tampered client can't inject
     quote text that was never sent. */
  const [replyingTo, setReplyingTo] = useState(null);
  const composerRef = useRef(null);
  /* Message id to flash after a jump-to-original, cleared by a timer. */
  const [highlightedMsgId, setHighlightedMsgId] = useState(null);
  const highlightTimerRef = useRef(null);
  /* _id → bubble row element, for scrolling a quoted original into view. */
  const bubbleRefs = useRef(new Map());

  const openMsgMenu = useCallback((message, anchorEl) => {
    const rect = anchorEl.getBoundingClientRect();
    const MENU_H = 96; // approx menu height for flip decision
    const openUp = window.innerHeight - rect.bottom < MENU_H + 16;
    setMenuPos({
      rightOffset: Math.max(8, window.innerWidth - rect.right),
      ...(openUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    });
    setMenuMsg(message);
  }, []);

  /* ── New Message compose state ───────────────────────────────────────────── */
  const [composing, setComposing] = useState(false);
  const [composePhone, setComposePhone] = useState("");
  const [composeQuery, setComposeQuery] = useState("");
  const [composeResults, setComposeResults] = useState([]);
  const [composeSearching, setComposeSearching] = useState(false);
  const [composeRecipient, setComposeRecipient] = useState(null); // { name, phone } or null for external
  const [composeText, setComposeText] = useState("");
  const [composeSending, setComposeSending] = useState(false);

  /* Template send modal — 'reply' (re-open an open chat) | 'compose' (new number) | null */
  const [templateMode, setTemplateMode] = useState(null);
  const [showInteractive, setShowInteractive] = useState(false);

  // Citation drill-down popover for AI replies grounded in KB chunks
  const [citationPopover, setCitationPopover] = useState(null); // { messageId, ids, items, loading, error }

  // AI Insights drill-down for the selected conversation
  const [aiInsights, setAiInsights] = useState(null); // { open, loading, data, error }
  const composeSearchTimer = useRef(null);

  const handleStartCompose = () => {
    setComposing(true);
    setSelected(null);
    setComposePhone("");
    setComposeQuery("");
    setComposeResults([]);
    setComposeRecipient(null);
    setComposeText("");
  };

  const handleCloseCompose = useCallback(() => {
    setComposing(false);
    setComposePhone("");
    setComposeQuery("");
    setComposeResults([]);
    setComposeRecipient(null);
    setComposeText("");
  }, []);

  const handleComposeSearch = (value) => {
    setComposeQuery(value);
    if (composeSearchTimer.current) clearTimeout(composeSearchTimer.current);
    if (!composeAllowStudentSearch) { setComposeResults([]); setComposeSearching(false); return; }
    if (value.trim().length < 2) { setComposeResults([]); setComposeSearching(false); return; }
    setComposeSearching(true);
    composeSearchTimer.current = setTimeout(async () => {
      try {
        const { data } = await apiClient.get(`/whatsapp/students/search?q=${encodeURIComponent(value.trim())}`);
        setComposeResults(data.students || []);
      } catch { setComposeResults([]); }
      setComposeSearching(false);
    }, 350);
  };

  const handleComposeSelectStudent = (student) => {
    setComposeRecipient({ name: student.name, phone: student.mobile_number, studentId: student._id });
    setComposePhone(student.mobile_number || "");
    setComposeQuery("");
    setComposeResults([]);
  };

  // Send a brand-new message. Defaults to the institute endpoint; the platform
  // inbox overrides this via the sendNewMessage prop.
  const doSendNewMessage = useCallback(async (payload) => {
    if (sendNewMessage) { await sendNewMessage(payload); return; }
    await apiClient.post("/whatsapp/send-message", payload);
  }, [sendNewMessage]);

  // Attach the chosen recipient (student or external phone) to a compose payload.
  const withComposeRecipient = useCallback((payload) => {
    const phone = composePhone.trim();
    if (composeRecipient?.studentId) return { ...payload, studentId: composeRecipient.studentId };
    return { ...payload, toPhone: phone };
  }, [composePhone, composeRecipient]);

  const composeHasRecipient = !!composeRecipient || composePhone.trim().length >= 10;

  const handleComposeSend = async () => {
    const phone = composePhone.trim();
    if ((!composeRecipient && phone.length < 10) || !composeText.trim()) return;
    setComposeSending(true);
    try {
      await doSendNewMessage(withComposeRecipient({ messageType: "text", text: composeText.trim() }));
      showToast("Message sent");
      handleCloseCompose();
      loadConversations(false);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to send", "error");
    }
    setComposeSending(false);
  };

  /* ── Load conversations ──────────────────────────────────────────────────
     Re-reads the NEWEST page and merges it in, so a refresh or a socket ping
     picks up new activity without throwing away the older pages the admin has
     already scrolled to. `hasMore` is only taken from this when page 1 is all
     that is loaded — otherwise the deepest page loaded is the one that knows
     whether anything is left, and page 1 always says yes. */
  const loadConversations = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetchConversations(statusFilterRef.current, {
        page: 1, limit: CONVERSATION_PAGE_SIZE, search: searchRef.current,
      });
      const { list, hasMore, paginated } = normalizeConversationPage(res);
      setServerPaged(paginated);
      setConversations(prev => (prev.length ? mergeConversations(prev, list) : list));
      if (convPageRef.current === 1) {
        hasMoreConvsRef.current = hasMore;
        setHasMoreConvs(hasMore);
      }
    } catch { /* ignore */ }
    if (!silent) setLoading(false);
  }, [fetchConversations]);

  /* Search settles before it reaches the server. */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchTerm]);

  /* A changed filter or search is a different list, so this one REPLACES rather
     than merges, and rewinds the paging cursor with it. The cancelled flag
     matters because a fast typist has several of these in flight at once and
     the last request to be sent is not always the last to return. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      convPageRef.current = 1;
      hasMoreConvsRef.current = false;
      loadingMoreConvsRef.current = false;
      setLoadingMoreConvs(false);
      try {
        const res = await fetchConversations(statusFilter, {
          page: 1, limit: CONVERSATION_PAGE_SIZE, search: debouncedSearch,
        });
        if (cancelled) return;
        const { list, hasMore, paginated } = normalizeConversationPage(res);
        setServerPaged(paginated);
        setConversations(list);
        hasMoreConvsRef.current = hasMore;
        setHasMoreConvs(hasMore);
        // A new list starts at the top; leaving the old offset would land the
        // reader in the middle of results they have not seen.
        if (convScrollRef.current) convScrollRef.current.scrollTop = 0;
      } catch { /* ignore */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [fetchConversations, statusFilter, debouncedSearch]);

  /* ── Load the next older page of chats ──────────────────────────────────── */
  const loadMoreConversations = useCallback(async () => {
    if (loadingMoreConvsRef.current || !hasMoreConvsRef.current) return;
    loadingMoreConvsRef.current = true;
    setLoadingMoreConvs(true);
    const nextPage = convPageRef.current + 1;
    try {
      const res = await fetchConversations(statusFilterRef.current, {
        page: nextPage, limit: CONVERSATION_PAGE_SIZE, search: searchRef.current,
      });
      const { list, hasMore } = normalizeConversationPage(res);
      convPageRef.current = nextPage;
      hasMoreConvsRef.current = hasMore;
      setHasMoreConvs(hasMore);
      setConversations(prev => mergeConversations(prev, list));
    } catch { /* ignore — the button and the next scroll both retry */ }
    finally {
      loadingMoreConvsRef.current = false;
      setLoadingMoreConvs(false);
    }
  }, [fetchConversations]);

  /* Pull the next page as the list nears its end. The button below is not a
     fallback for a broken handler — a sidebar taller than one page never
     scrolls at all, so without it there is nothing to trigger. */
  const handleConvScroll = useCallback((e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= LOAD_MORE_CONVS_AT_PX) {
      loadMoreConversations();
    }
  }, [loadMoreConversations]);

  /* ── Re-sync the newest page of the open conversation ────────────────────
     Used after sending, deleting, or a socket ping. Merges instead of
     replacing so history the user scrolled back through isn't discarded.
     Deliberately does NOT touch hasMoreOlder: that tracks how far back we've
     paged, which page 1 knows nothing about. */
  const refreshLatestMessages = useCallback(async ({ scrollToBottom = false } = {}) => {
    const conv = selectedRef.current;
    if (!conv) return;
    try {
      const res = await fetchMessages(conv._id, { page: 1, limit: MESSAGE_PAGE_SIZE });
      // The user may have switched chats while this was in flight.
      if (selectedRef.current?._id !== conv._id) return;
      const { list } = normalizeMessagePage(res);
      setMessages(prev => mergeMessages(prev, list));
      // Never yank someone who has scrolled up to read history.
      if (scrollToBottom || nearBottomRef.current) autoScrollRef.current = "smooth";
    } catch { /* ignore — the list simply stays as-is */ }
  }, [fetchMessages]);

  /* ── Load the next older page (scroll-up history) ───────────────────────── */
  const loadOlderMessages = useCallback(async () => {
    const conv = selectedRef.current;
    if (!conv || loadingOlderRef.current || !hasMoreOlderRef.current) return;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    const nextPage = msgPageRef.current + 1;
    try {
      const res = await fetchMessages(conv._id, { page: nextPage, limit: MESSAGE_PAGE_SIZE });
      if (selectedRef.current?._id !== conv._id) return;
      const { list, hasMore } = normalizeMessagePage(res);
      msgPageRef.current = nextPage;
      hasMoreOlderRef.current = hasMore;
      setHasMoreOlder(hasMore);
      if (list.length) {
        // Measure immediately before the prepend so the layout effect can
        // restore the exact scroll offset once the taller list is laid out.
        const el = messagesScrollRef.current;
        if (el) restoreScrollRef.current = { prevHeight: el.scrollHeight, prevTop: el.scrollTop };
        setMessages(prev => mergeMessages(list, prev));
      }
    } catch { /* ignore — the user can scroll up again to retry */ }
    finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, [fetchMessages]);

  /* Track how close to the bottom we are, and pull history near the top. */
  const handleMessagesScroll = useCallback((e) => {
    const el = e.currentTarget;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
    if (el.scrollTop <= LOAD_OLDER_AT_PX) loadOlderMessages();
  }, [loadOlderMessages]);

  /* ── Template sends ──────────────────────────────────────────────────────── */
  // Reply bar: send a template into the open conversation (re-opens an expired
  // 24h window). Throws on failure so the modal surfaces the error.
  const handleReplyTemplateSend = useCallback(async (tpl) => {
    if (!selected) return;
    await sendReply(selected, { messageType: "template", ...tpl });
    await refreshLatestMessages({ scrollToBottom: true });
    loadConversations(true);
    showToast("Template sent");
  }, [selected, sendReply, refreshLatestMessages, loadConversations, showToast]);

  // Compose panel: send a template to a brand-new number / student.
  const handleComposeTemplateSend = useCallback(async (tpl) => {
    await doSendNewMessage(withComposeRecipient({ messageType: "template", ...tpl }));
    showToast("Template sent");
    handleCloseCompose();
    loadConversations(false);
  }, [doSendNewMessage, withComposeRecipient, showToast, loadConversations, handleCloseCompose]);

  // Real-time updates via Socket.IO
  useEffect(() => {
    if (!socket) return;
    const handler = () => {
      loadConversations(true);
      // If a conversation is open, pull the newest page in too.
      if (selectedRef.current) refreshLatestMessages();
    };
    socket.on('whatsapp:newMessage', handler);
    return () => socket.off('whatsapp:newMessage', handler);
  }, [socket, loadConversations, refreshLatestMessages]);

  /* ── Scroll position management ──────────────────────────────────────────
     Runs before paint so neither correction is ever visible as a jump.
     A pending restore always wins: prepending history must never scroll the
     reader away from the message they were looking at. */
  useLayoutEffect(() => {
    const el = messagesScrollRef.current;
    const restore = restoreScrollRef.current;
    if (el && restore) {
      restoreScrollRef.current = null;
      // Older messages were prepended above: keep the same content under the
      // viewport by absorbing exactly the height that was just added.
      el.scrollTop = el.scrollHeight - restore.prevHeight + restore.prevTop;
      return;
    }
    const behavior = autoScrollRef.current;
    autoScrollRef.current = null;
    if (!behavior) return;
    messagesEndRef.current?.scrollIntoView({ behavior, block: "end" });
  }, [messages]);

  /* ── Open conversation ──────────────────────────────────────────────────── */
  const openConversation = useCallback(async (conv) => {
    setSelected(conv);
    selectedRef.current = conv; // so in-flight guards below compare correctly
    setShowDetails(false);
    setMsgLoading(true);
    // Reset history pagination for the new chat.
    msgPageRef.current = 1;
    hasMoreOlderRef.current = false;
    loadingOlderRef.current = false;
    restoreScrollRef.current = null;
    nearBottomRef.current = true;
    setHasMoreOlder(false);
    setLoadingOlder(false);
    setMessages([]);
    /* A quote only means anything inside the chat it came from — carrying it
       across would send the wrong number a reply to a message they can't see
       (and the server would reject it anyway). */
    setReplyingTo(null);
    setHighlightedMsgId(null);
    bubbleRefs.current.clear();
    let superseded = false;
    try {
      const res = await fetchMessages(conv._id, { page: 1, limit: MESSAGE_PAGE_SIZE });
      // A newer openConversation owns the loading flag from here on.
      if (selectedRef.current?._id !== conv._id) { superseded = true; return; }
      const { list, hasMore } = normalizeMessagePage(res);
      hasMoreOlderRef.current = hasMore;
      setHasMoreOlder(hasMore);
      // A chat opens pinned to the bottom, without an animated scroll.
      autoScrollRef.current = "auto";
      setMessages(list);
      // Opening marks the conversation read on the server. Reflect that in the
      // local list immediately and re-sync the sidebar badge so it updates
      // without a page reload.
      if (conv.unreadCount > 0) {
        setConversations(prev =>
          prev.map(c => (c._id === conv._id ? { ...c, unreadCount: 0 } : c)));
        onConversationRead?.();
      }
    } catch { /* ignore */ }
    finally { if (!superseded) setMsgLoading(false); }
  }, [fetchMessages, onConversationRead]);

  /* ── Notes: load when the contact-info drawer is opened ─────────────────── */
  const loadNotes = useCallback(async (convId) => {
    if (!notesEnabled || !convId) { setNotes([]); return; }
    setNotesLoading(true);
    try {
      const data = await fetchNotes(convId);
      setNotes(Array.isArray(data) ? data : []);
    } catch {
      setNotes([]);
    }
    setNotesLoading(false);
  }, [notesEnabled, fetchNotes]);

  // Load on drawer open; reset editing state whenever the contact changes.
  useEffect(() => {
    if (notesEnabled && showDetails && selected?._id) {
      loadNotes(selected._id);
    }
    setEditingNoteId(null);
    setEditingNoteText("");
    setNewNote("");
  }, [notesEnabled, showDetails, selected?._id, loadNotes]);

  const handleAddNote = async () => {
    const body = newNote.trim();
    if (!body || !selected?._id) return;
    setSavingNote(true);
    try {
      await addNote(selected._id, body);
      setNewNote("");
      await loadNotes(selected._id);
    } catch (err) {
      showToast(err?.response?.data?.message || err?.message || "Failed to add note", "error");
    }
    setSavingNote(false);
  };

  const handleSaveEditNote = async (note) => {
    const body = editingNoteText.trim();
    if (!body) return;
    setNoteBusyId(note._id);
    try {
      await editNote(note._id, body);
      setEditingNoteId(null);
      setEditingNoteText("");
      await loadNotes(selected._id);
    } catch (err) {
      showToast(err?.response?.data?.message || err?.message || "Failed to update note", "error");
    }
    setNoteBusyId(null);
  };

  const handleConfirmDeleteNote = async () => {
    if (!confirmDeleteNote) return;
    const note = confirmDeleteNote;
    setNoteBusyId(note._id);
    try {
      await deleteNote(note._id);
      setConfirmDeleteNote(null);
      setNotes((prev) => prev.filter((n) => n._id !== note._id));
    } catch (err) {
      showToast(err?.response?.data?.message || err?.message || "Failed to delete note", "error");
    }
    setNoteBusyId(null);
  };

  /* ── AI: Load pending drafts for selected conversation ─────────────────── */
  const loadAIDrafts = useCallback(async (convId) => {
    if (!convId) { setAiDrafts([]); return; }
    try {
      const { data } = await apiClient.get(`/ai/whatsapp/drafts?status=pending&conversationId=${convId}`);
      setAiDrafts(data.drafts || []);
    } catch (err) {
      // 402/403 = no plan; silently ignore
      if (err?.response?.status !== 402 && err?.response?.status !== 403) {
        // non-fatal
      }
      setAiDrafts([]);
    }
  }, []);

  useEffect(() => { loadAIDrafts(selected?._id); }, [selected?._id, loadAIDrafts]);

  /* ── AI: socket listeners for new drafts / handoffs ──────────────────────── */
  useEffect(() => {
    if (!socket) return;
    const onDraft = (payload) => {
      if (selected?._id === payload?.conversationId) {
        loadAIDrafts(selected._id);
      }
    };
    const onHandoff = (payload) => {
      if (selected?._id === payload?.conversationId) {
        setSelected(prev => prev ? { ...prev, aiPaused: true, aiHandoffReason: payload?.reason || 'AI handoff' } : prev);
        showToast(`AI paused for this conversation: ${payload?.reason || 'handoff'}`, 'info');
      }
      loadConversations(true);
    };
    socket.on('whatsapp:ai:draft', onDraft);
    socket.on('whatsapp:ai:handoff', onHandoff);
    return () => {
      socket.off('whatsapp:ai:draft', onDraft);
      socket.off('whatsapp:ai:handoff', onHandoff);
    };
  }, [socket, selected?._id, loadAIDrafts, loadConversations, showToast]);

  /* ── AI: send / discard draft ───────────────────────────────────────────── */
  const handleSendAIDraft = async (draft) => {
    setAiDraftBusy(b => ({ ...b, [draft._id]: 'sending' }));
    try {
      const editedText = (aiDraftEdit[draft._id] || '').trim();
      const body = editedText && editedText !== draft.draftText ? { editedText } : {};
      await apiClient.post(`/ai/whatsapp/drafts/${draft._id}/send`, body);
      showToast('AI reply sent', 'success');
      setAiDrafts(prev => prev.filter(d => d._id !== draft._id));
      setAiDraftEdit(prev => { const n = { ...prev }; delete n[draft._id]; return n; });
      // Refresh messages
      if (selected?._id) {
        await refreshLatestMessages({ scrollToBottom: true });
        loadConversations(true);
      }
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to send AI draft', 'error');
    } finally {
      setAiDraftBusy(b => { const n = { ...b }; delete n[draft._id]; return n; });
    }
  };
  const handleDiscardAIDraft = async (draft) => {
    setAiDraftBusy(b => ({ ...b, [draft._id]: 'discarding' }));
    try {
      await apiClient.post(`/ai/whatsapp/drafts/${draft._id}/discard`);
      setAiDrafts(prev => prev.filter(d => d._id !== draft._id));
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to discard', 'error');
    } finally {
      setAiDraftBusy(b => { const n = { ...b }; delete n[draft._id]; return n; });
    }
  };

  /* ── AI: pause / resume ──────────────────────────────────────────────────── */
  const handleAIPauseToggle = async () => {
    if (!selected) return;
    setAiPauseBusy(true);
    try {
      const path = selected.aiPaused ? 'resume' : 'pause';
      const { data } = await apiClient.post(`/ai/whatsapp/conversations/${selected._id}/${path}`);
      setSelected(prev => ({
        ...prev,
        aiPaused: !!data.conversation?.aiPaused,
        aiHandoffReason: data.conversation?.aiHandoffReason,
      }));
      showToast(selected.aiPaused ? 'AI resumed for this conversation' : 'AI paused for this conversation', 'success');
      loadConversations(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402 || status === 403) {
        showToast('AI WhatsApp Agent not enabled on your plan', 'error');
      } else {
        showToast(err?.response?.data?.message || 'Failed to toggle AI', 'error');
      }
    } finally {
      setAiPauseBusy(false);
    }
  };

  /* ── AI: open citation drill-down ─────────────────────────────────────── */
  const setAiOverride = async (override) => {
    if (!selected) return;
    setAiPauseBusy(true);
    try {
      const { data } = await apiClient.post(`/ai/whatsapp/conversations/${selected._id}/ai-override`, { override });
      setSelected(prev => ({ ...prev, aiOverride: data.aiOverride }));
      const labels = { 'inherit': 'follow global setting', 'force-on': 'always reply (force on)', 'force-off': 'never reply (force off)' };
      showToast(`AI override: ${labels[data.aiOverride] || data.aiOverride}`, 'success');
      loadConversations(true);
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to set override', 'error');
    } finally {
      setAiPauseBusy(false);
    }
  };

  const openCitationPopover = async (messageId, ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return;
    setCitationPopover({ messageId, ids, items: [], loading: true, error: null });
    try {
      const { data } = await apiClient.get(`/ai/whatsapp/knowledge/by-ids?ids=${ids.join(',')}`);
      setCitationPopover({ messageId, ids, items: data?.items || [], loading: false, error: null });
    } catch (err) {
      setCitationPopover({
        messageId, ids, items: [], loading: false,
        error: err?.response?.data?.message || 'Failed to load knowledge chunks',
      });
    }
  };

  const closeCitationPopover = () => setCitationPopover(null);

  /* ── AI: open conversation insights drill-down ────────────────────────── */
  const openAIInsights = async () => {
    if (!selected) return;
    setAiInsights({ open: true, loading: true, data: null, error: null });
    try {
      const { data } = await apiClient.get(`/ai/whatsapp/conversations/${selected._id}/analytics`);
      setAiInsights({ open: true, loading: false, data, error: null });
    } catch (err) {
      setAiInsights({
        open: true, loading: false, data: null,
        error: err?.response?.data?.message || 'Failed to load AI insights',
      });
    }
  };
  const closeAIInsights = () => setAiInsights(null);



  /* Close the per-message menu on any outside click / scroll / Escape. */
  useEffect(() => {
    if (!menuMsg) return undefined;
    const close = () => setMenuMsg(null);
    const onKey = (e) => { if (e.key === "Escape") setMenuMsg(null); };
    document.addEventListener("click", close);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true); // capture: catch inner scrolls
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [menuMsg]);

  /* ── Quote a message in the composer ─────────────────────────────────────── */
  const handleStartReply = useCallback((message) => {
    setMenuMsg(null);
    /* A tombstoned message has no body left to quote — the server strips it and
       would reject the send anyway. */
    if (!message || message.deleted) return;
    setReplyingTo(message);
    // Focus lands on the composer so typing can start immediately, matching the
    // swipe gesture's expectation on mobile.
    requestAnimationFrame(() => composerRef.current?.focus());
  }, []);

  const handleCancelReply = useCallback(() => setReplyingTo(null), []);

  /* ── Swipe-to-reply (touch) ──────────────────────────────────────────────
     WhatsApp lets you drag a bubble to the right and releasing past a threshold
     quotes it. Implemented with raw touch events rather than a gesture library
     to keep it dependency-free and, more importantly, to stay passive until the
     drag is unambiguously horizontal — claiming the gesture any earlier fights
     the message list's vertical scroll, which is the far more common intent. */
  const [swipeMsgId, setSwipeMsgId] = useState(null);
  const [swipeDx, setSwipeDx] = useState(0);
  /* { x, y, id, locked, dx }. `dx` is mirrored here, not read back from state,
     so the release check can't act on a stale render's value. */
  const swipeStart = useRef(null);

  const swipeHandlersFor = useCallback((message) => {
    if (!canReply || !message?._id || message.deleted) return {};
    return {
      onTouchStart: (e) => {
        const t = e.touches[0];
        swipeStart.current = { x: t.clientX, y: t.clientY, id: String(message._id), locked: false, dx: 0 };
      },
      onTouchMove: (e) => {
        const s = swipeStart.current;
        if (!s) return;
        const t = e.touches[0];
        const dx = t.clientX - s.x;
        const dy = t.clientY - s.y;

        if (!s.locked) {
          // Vertical intent wins outright — abandon so the list scrolls normally.
          if (Math.abs(dy) > Math.abs(dx)) { swipeStart.current = null; return; }
          if (dx < SWIPE_LOCK_PX) return;
          s.locked = true;
          setSwipeMsgId(s.id);
        }
        // Right-drag only, with resistance past the ceiling.
        const eased = dx <= 0 ? 0 : Math.min(dx, SWIPE_MAX_PX + (dx - SWIPE_MAX_PX) * 0.2);
        s.dx = Math.min(eased, SWIPE_MAX_PX);
        setSwipeDx(s.dx);
      },
      onTouchEnd: () => {
        const s = swipeStart.current;
        swipeStart.current = null;
        setSwipeMsgId(null);
        setSwipeDx(0);
        if (s?.locked && s.dx >= SWIPE_TRIGGER_PX) handleStartReply(message);
      },
      onTouchCancel: () => {
        swipeStart.current = null;
        setSwipeMsgId(null);
        setSwipeDx(0);
      },
    };
  }, [canReply, handleStartReply]);

  /* ── Jump to a quoted original ───────────────────────────────────────────── */
  const handleJumpToMessage = useCallback((messageId) => {
    const el = bubbleRefs.current.get(String(messageId));
    if (!el) {
      /* The original is older than the pages loaded so far. Paging back to find
         it could mean many round-trips, so say so rather than silently doing
         nothing — the user pressed something and deserves a response. */
      showToast("Scroll up to load older messages to see the original", "info");
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedMsgId(String(messageId));
    clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => setHighlightedMsgId(null), 1600);
  }, [showToast]);

  useEffect(() => () => clearTimeout(highlightTimerRef.current), []);

  /* ── Copy message text to clipboard ───────────────────────────────────────── */
  const handleCopyMessage = async (message) => {
    setMenuMsg(null);
    const text = message?.content || "";
    if (!text) { showToast("Nothing to copy", "info"); return; }
    try {
      await navigator.clipboard.writeText(text);
      showToast("Copied to clipboard");
    } catch {
      showToast("Couldn't copy", "error");
    }
  };

  /* ── Delete message (delete-from-inbox, local only) ───────────────────────── */
  const handleConfirmDelete = async () => {
    if (!confirmDeleteMsg || !deleteMessage) return;
    setDeletingMsg(true);
    try {
      await deleteMessage(confirmDeleteMsg);
      // Optimistically tombstone in place, then refresh from server.
      setMessages((prev) =>
        prev.map((m) => (m._id === confirmDeleteMsg._id ? { ...m, deleted: true } : m)));
      setConfirmDeleteMsg(null);
      if (selected?._id) {
        refreshLatestMessages();
        loadConversations(true);
      }
    } catch (err) {
      showToast(err?.response?.data?.message || err?.message || "Failed to delete message", "error");
    }
    setDeletingMsg(false);
  };

  /* ── Send reply ─────────────────────────────────────────────────────────── */
  const handleSendReply = async () => {
    if (!replyText.trim() || !selected) return;
    setSending(true);
    try {
      await sendReply(selected, {
        messageType: "text",
        text: replyText.trim(),
        ...(replyingTo ? { replyTo: replyingTo._id } : {}),
      });
      setReplyText("");
      // Only drop the quote once the send succeeded — on failure the user still
      // has their text AND the message they were answering.
      setReplyingTo(null);
      // Refresh messages
      await refreshLatestMessages({ scrollToBottom: true });
      loadConversations(true);
    } catch (err) {
      showToast(err?.response?.data?.message || err?.message || "Failed to send", "error");
    }
    setSending(false);
  };

  /* ── Attachments ──────────────────────────────────────────────────────────
     Picking a file does NOT send it. Images, videos and documents land in a
     staging tray that renders a full-pane preview over the chat — the WhatsApp
     flow — so a caption can be typed, the wrong file dropped and more files
     added before anything leaves the browser.

     Stickers are the one exception and still send straight away: WhatsApp has
     no sticker caption, so a preview would offer nothing to edit. */
  const mediaInputRef = useRef(null);
  const stickerInputRef = useRef(null);
  const docInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  /* The composer's "+" menu. One button replaces the five icons that used to
     live in the reply bar — on a phone they left the text field about 40% of
     the row. */
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);

  /* { kind: 'media' | 'document', items: [{ id, file, url, caption }], activeId }
     `kind` only decides which picker the tray's "add more" tile opens; a tray
     can hold a mix once the user adds to it, and each item is uploaded and sent
     as its own message. `url` is an object URL for previewable types and null
     for documents, which are represented by name + size. */
  const [pendingMedia, setPendingMedia] = useState(null);
  /* { done, total } while the tray is being sent — also the "busy" flag. */
  const [mediaSending, setMediaSending] = useState(null);
  const pendingIdRef = useRef(0);

  const buildPendingItems = (files) =>
    Array.from(files || []).map((file) => ({
      id: `pm-${++pendingIdRef.current}`,
      file,
      url: isPreviewable(file.type) ? URL.createObjectURL(file) : null,
      caption: "",
    }));

  const handleFilesPicked = (files, kind) => {
    const picked = buildPendingItems(files);
    if (!picked.length) return;
    setAttachMenuOpen(false);
    // Adding to an open tray jumps to the first new file — that is the one the
    // user just chose and almost certainly wants to caption.
    setPendingMedia((prev) => (prev
      ? { ...prev, items: [...prev.items, ...picked], activeId: picked[0].id }
      : { kind, items: picked, activeId: picked[0].id }));
  };

  /* Object URLs are revoked as items leave the tray rather than on unmount
     alone, so a long session of picking and discarding doesn't accumulate
     blobs the tab can never free. */
  const revokeItems = (items) =>
    items.forEach((it) => { if (it.url) URL.revokeObjectURL(it.url); });

  const discardPendingMedia = () => {
    if (!pendingMedia) return;
    revokeItems(pendingMedia.items);
    setPendingMedia(null);
  };

  const removePendingItem = (id) => {
    if (!pendingMedia) return;
    const gone = pendingMedia.items.find((it) => it.id === id);
    const items = pendingMedia.items.filter((it) => it.id !== id);
    if (gone) revokeItems([gone]);
    if (!items.length) { setPendingMedia(null); return; }
    setPendingMedia({
      ...pendingMedia,
      items,
      activeId: pendingMedia.activeId === id ? items[0].id : pendingMedia.activeId,
    });
  };

  const setPendingCaption = (id, caption) =>
    setPendingMedia((prev) => (prev
      ? { ...prev, items: prev.items.map((it) => (it.id === id ? { ...it, caption } : it)) }
      : prev));

  /* Uploads and sends one message per file, in tray order. On a mid-batch
     failure the files that already went out are dropped from the tray and the
     rest are kept, so retrying re-sends only what didn't make it. */
  const handleSendPendingMedia = async () => {
    if (!pendingMedia || !selected || mediaSending) return;
    const items = pendingMedia.items;
    const kind = pendingMedia.kind;
    setMediaSending({ done: 0, total: items.length });
    let sent = 0;
    try {
      for (const item of items) {
        const form = new FormData();
        form.append("file", item.file);
        const { data } = await apiClient.post(uploadEndpoint, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        await sendReply(selected, {
          messageType: resolveMessageType(data.mimeType || item.file.type),
          mediaId: data.mediaId,
          mimeType: data.mimeType,
          filename: data.filename,
          caption: item.caption.trim() || undefined,
          // A quote belongs to one message, so it rides the first file only.
          ...(replyingTo && sent === 0 ? { replyTo: replyingTo._id } : {}),
        });
        sent += 1;
        setMediaSending({ done: sent, total: items.length });
      }
      revokeItems(items);
      setPendingMedia(null);
      setReplyingTo(null);
    } catch (err) {
      showToast(err?.response?.data?.message || err?.message || "Failed to send attachment", "error");
      const remaining = items.slice(sent);
      revokeItems(items.slice(0, sent));
      setPendingMedia(remaining.length
        ? { kind, items: remaining, activeId: remaining[0].id }
        : null);
      if (sent) setReplyingTo(null);
    }
    setMediaSending(null);
    await refreshLatestMessages({ scrollToBottom: true });
    loadConversations(true);
  };

  /* Stickers only — see the note above. */
  const handleStickerPicked = async (file) => {
    if (!file || !selected) return;
    setAttachMenuOpen(false);
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await apiClient.post(uploadEndpoint, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await sendReply(selected, {
        messageType: "sticker",
        mediaId: data.mediaId,
        mimeType: data.mimeType,
        filename: data.filename,
        ...(replyingTo ? { replyTo: replyingTo._id } : {}),
      });
      setReplyingTo(null);
      await refreshLatestMessages({ scrollToBottom: true });
      loadConversations(true);
    } catch (err) {
      showToast(err?.response?.data?.message || err?.message || "Failed to send attachment", "error");
    }
    setUploading(false);
  };

  /* Mirror of the tray for cleanup paths that must not depend on the closure
     of the render that scheduled them (unmount, conversation switch). */
  const pendingMediaRef = useRef(null);
  useEffect(() => { pendingMediaRef.current = pendingMedia; }, [pendingMedia]);

  useEffect(() => () => {
    if (pendingMediaRef.current) revokeItems(pendingMediaRef.current.items);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Staged files belong to the conversation they were picked in — leaving it
     drops them rather than risking a send to the next contact opened. */
  useEffect(() => {
    if (pendingMediaRef.current) revokeItems(pendingMediaRef.current.items);
    setPendingMedia(null);
    setAttachMenuOpen(false);
  }, [selected?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!pendingMedia && !attachMenuOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (attachMenuOpen) { setAttachMenuOpen(false); return; }
      if (pendingMedia && !mediaSending) discardPendingMedia();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pendingMedia, attachMenuOpen, mediaSending]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Status change ──────────────────────────────────────────────────────── */
  const handleStatusChange = async (convId, status) => {
    try {
      await updateStatus(convId, status);
      loadConversations(true);
      if (selected?._id === convId) setSelected(prev => ({ ...prev, status }));
      showToast(`Conversation ${status}`);
    } catch (err) {
      showToast(err?.response?.data?.message || "Could not change the conversation's status. Please try again.", "error");
    }
  };

  /* Conversation controls (AI + status). Shared between the desktop header
     (inline) and the contact-details drawer behind the kebab, so they stay in
     sync.

     `variant` is "drawer" for the panel and "inline" for the header. The AI
     override <select> is drawer-only: its longest option ("AI: Force ON (always
     auto-reply)") is wider than the rest of the row put together, and it is the
     least-used control here — inline it was what pushed the header past its
     width and made the controls collide. */
  /* Convert to enquiry. The server links the chat and names it after the person,
     so both the open chat and its row in the list take the enquiry and the name. */
  const [convertOpen, setConvertOpen] = useState(false);
  useEffect(() => { setConvertOpen(false); }, [selected?._id]);
  const handleEnquiryLinked = (enquiry, { linked, message } = {}) => {
    if (!enquiry?._id || !selected) return;
    const update = (c) => (c._id === selected._id
      ? {
        ...c,
        enquiryId: enquiry._id,
        enquiryName: enquiry.fullName || c.enquiryName,
        enquiryStatus: enquiry.status || c.enquiryStatus,
        ...(linked ? {} : { name: enquiry.fullName || c.name }),
      }
      : c);
    setSelected((prev) => (prev ? update(prev) : prev));
    setConversations((prev) => prev.map(update));
    setConvertOpen(false);
    showToast(message || (linked ? "Chat linked to the enquiry" : "Enquiry created"), "success");
  };

  const renderConvControls = (variant = "drawer") => (
    <>
      {!canReply ? null : selected.aiPaused ? (
        <button
          className="wai-action-btn"
          onClick={handleAIPauseToggle}
          disabled={aiPauseBusy}
          title={selected.aiHandoffReason || 'AI is paused for this conversation'}
        >
          {aiPauseBusy ? <Loader2 size={14} className="wai-spin" /> : <Play size={14} />} Resume AI
        </button>
      ) : (
        <button
          className="wai-action-btn"
          onClick={handleAIPauseToggle}
          disabled={aiPauseBusy}
          title="Pause AI auto-replies for this conversation"
        >
          {aiPauseBusy ? <Loader2 size={14} className="wai-spin" /> : <Pause size={14} />} Pause AI
        </button>
      )}
      <button className="wai-action-btn" onClick={openAIInsights} title="View AI metrics for this conversation">
        <BarChart3 size={14} /> AI Insights
      </button>
      {canReply && variant === "drawer" && (
        <select
          className="wai-action-btn wai-action-btn--select"
          value={selected.aiOverride || 'inherit'}
          onChange={(e) => setAiOverride(e.target.value)}
          disabled={aiPauseBusy}
          title="Per-conversation AI override (overrides global mode for this contact only)"
        >
          <option value="inherit">AI: Inherit global</option>
          <option value="force-on">AI: Force ON (always auto-reply)</option>
          <option value="force-off">AI: Force OFF (never reply)</option>
        </select>
      )}
      {canReply && selected.status !== "resolved" && (
        <button className="wai-action-btn" onClick={() => handleStatusChange(selected._id, "resolved")}>
          <Check size={14} /> Resolve
        </button>
      )}
      {canReply && selected.status !== "closed" && (
        <button className="wai-action-btn wai-action-btn--close" onClick={() => handleStatusChange(selected._id, "closed")}>
          <XCircle size={14} /> Close
        </button>
      )}
    </>
  );

  /* ── Filter conversations ───────────────────────────────────────────────
     Only when the caller does NOT page. A server that answered the search has
     already matched more than this can — a number typed with a country code and
     spaces, a student whose name is not denormalised onto the row — so
     re-filtering its results would silently drop correct hits. */
  const filtered = serverPaged ? conversations : conversations.filter(c => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (c.name || "").toLowerCase().includes(q)
      || (c.phone || "").includes(q)
      || (c.studentName || "").toLowerCase().includes(q);
  });
  const searching = serverPaged && !!debouncedSearch;

  /* Memoised: the list grows without bound as history is paged in. */
  const grouped = useMemo(() => groupByDate(messages), [messages]);

  return (
    <div className="wai-container">
      {/* ════════ Left: Conversation list ════════ */}
      <div className={`wai-sidebar${selected ? " wai-sidebar--hidden-mobile" : ""}`}>
        <div className="wai-sidebar__header">
          <span className="wai-sidebar__title">Chats</span>
          <div className="wai-sidebar__actions">
            <select
              className="wai-filter-select"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="open">Open</option>
              <option value="assigned">Assigned</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <button className="wai-icon-btn" onClick={() => loadConversations(false)} title="Refresh">
              <RefreshCw size={16} />
            </button>
            {canReply && (
              <button className="wai-icon-btn" onClick={handleStartCompose} title="New message">
                <PenSquare size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Sending health. Above the search box rather than over the chat pane:
            it is a fact about the account, not about the open conversation, and
            it stays visible on mobile where the sidebar is the default view. */}
        {typeof fetchSendingHealth === "function" && (
          <WhatsAppSendingStatus fetchHealth={fetchSendingHealth} />
        )}

        <div className="wai-search">
          <Search size={15} className="wai-search__icon" />
          <input
            className="wai-search__input"
            placeholder="Search every chat by name or number"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            aria-label="Search conversations"
          />
          {searchTerm && (
            <button
              type="button"
              className="wai-search__clear"
              onClick={() => setSearchTerm("")}
              title="Clear search"
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="wai-conv-list" ref={convScrollRef} onScroll={handleConvScroll}>
          {loading ? <Loader /> : filtered.length === 0 ? (
            <div className="wai-empty-sidebar">
              <MessageCircle size={28} />
              <span>{searching ? `No chats match "${debouncedSearch}"` : "No conversations"}</span>
            </div>
          ) : (
            filtered.map(c => (
              <div
                key={c._id}
                role="button"
                tabIndex={0}
                className={`wai-conv-item${selected?._id === c._id ? " wai-conv-item--active" : ""}`}
                onClick={() => openConversation(c)}
                onKeyDown={e => { if (e.key === "Enter") openConversation(c); }}
              >
                <ChatAvatar name={c.name} phone={c.phone} />
                <div className="wai-conv-item__body">
                  <div className="wai-conv-item__top">
                    <span className="wai-conv-item__name">{c.name || c.phone}</span>
                    <span className="wai-conv-item__time">{formatChatTime(c.updatedAt)}</span>
                  </div>
                  <div className="wai-conv-item__bottom">
                    <span className="wai-conv-item__preview">{c.preview || "No messages yet"}</span>
                    {c.unreadCount > 0 && <span className="wai-unread-badge">{c.unreadCount}</span>}
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Older history. The scroll handler above pulls the next page on its
              own; this row is what makes that discoverable, and it is the only
              trigger there is when the list is shorter than the sidebar. */}
          {!loading && filtered.length > 0 && serverPaged && (
            hasMoreConvs ? (
              <button
                type="button"
                className="wai-conv-more"
                onClick={loadMoreConversations}
                disabled={loadingMoreConvs}
              >
                {loadingMoreConvs
                  ? <><Loader2 size={13} className="wai-spin" /> Loading older chats…</>
                  : <><ChevronDown size={13} /> Load older chats</>}
              </button>
            ) : (
              <div className="wai-conv-end">
                {searching ? "End of results" : "No older chats"}
              </div>
            )
          )}
        </div>
      </div>

      {/* ════════ Right: Chat area ════════ */}
      <div className={`wai-chat${selected ? " wai-chat--visible-mobile" : ""}`}>
        {composing ? (
          /* ── New Message compose panel ─── */
          <div className="wai-compose">
            <div className="wai-compose__header">
              <button className="wai-icon-btn" onClick={handleCloseCompose}><ArrowLeft size={18} /></button>
              <span className="wai-compose__title">New message</span>
            </div>

            {/* Recipient input */}
            <div className="wai-compose__to">
              <span className="wai-compose__to-label">To:</span>
              {composeRecipient ? (
                <div className="wai-compose__recipient-chip">
                  <User size={13} />
                  <span>{composeRecipient.name || composeRecipient.phone}</span>
                  <button className="wai-compose__chip-x" onClick={() => { setComposeRecipient(null); setComposePhone(""); }}>
                    <XCircle size={13} />
                  </button>
                </div>
              ) : (
                <input
                  className="wai-compose__to-input"
                  placeholder="Type a name or phone number"
                  value={composeQuery || composePhone}
                  onChange={e => {
                    const v = e.target.value;
                    // If it looks like a phone number (starts with digit or +), put it in phone
                    if (/^[+\d]/.test(v) && !/[a-zA-Z]/.test(v)) {
                      setComposePhone(v);
                      setComposeQuery("");
                      setComposeResults([]);
                    } else {
                      setComposePhone("");
                      handleComposeSearch(v);
                    }
                  }}
                  autoFocus
                />
              )}
            </div>

            {/* Search results dropdown */}
            {composeResults.length > 0 && !composeRecipient && (
              <div className="wai-compose__results">
                {composeResults.map(s => (
                  <div key={s._id} className="wai-compose__result-item" onClick={() => handleComposeSelectStudent(s)}>
                    <div className="wai-avatar" style={{ width: 32, height: 32, minWidth: 32, fontSize: '0.7rem' }}>
                      {(s.name || "?").split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: '0.85rem', color: 'var(--text-primary, #111b21)' }}>{s.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #667781)' }}>{s.mobile_number}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {composeSearching && (
              <div className="wai-loader" style={{ padding: '0.75rem 0' }}><Loader2 size={16} className="wai-spin" /> Searching…</div>
            )}

            {/* Message area */}
            <div className="wai-compose__body">
              <div className="wai-compose__body-empty">
                <MessageCircle size={40} strokeWidth={1} style={{ color: 'var(--text-tertiary, #8696a0)' }} />
                <p style={{ color: 'var(--text-tertiary, #8696a0)', fontSize: '0.85rem', margin: 0 }}>
                  {composeRecipient || composePhone.length >= 10
                    ? "Type your message below"
                    : "Search for a student or enter a phone number"}
                </p>
              </div>
            </div>

            {/* Compose reply bar */}
            <div className="wai-reply">
              {templatesEnabled && (
                <button
                  className="wai-reply__attach"
                  title={composeHasRecipient
                    ? "Send a template message"
                    : "Choose a recipient first, then send a template"}
                  onClick={() => setTemplateMode("compose")}
                  disabled={!composeHasRecipient}
                  style={{ background: "transparent", border: "none", color: "#54656f", padding: 6, cursor: composeHasRecipient ? "pointer" : "not-allowed" }}
                >
                  <FileText size={20} />
                </button>
              )}
              <AutoGrowTextarea
                className="wai-reply__input"
                value={composeText}
                onChange={e => setComposeText(e.target.value)}
                placeholder="Type a message"
                disabled={!composeRecipient && composePhone.trim().length < 10}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleComposeSend(); } }}
              />
              <button
                className="wai-reply__send"
                onClick={handleComposeSend}
                disabled={composeSending || !composeText.trim() || (!composeRecipient && composePhone.trim().length < 10)}
              >
                {composeSending ? <Loader2 size={18} className="wai-spin" /> : <Send size={18} />}
              </button>
            </div>
          </div>
        ) : !selected ? (
          <div className="wai-chat-empty">
            <div className="wai-chat-empty__icon"><MessageCircle size={56} strokeWidth={1} /></div>
            <h3>WhatsApp Inbox</h3>
            <p>Select a conversation to view messages</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="wai-chat__header">
              <button className="wai-back-btn" onClick={() => { setSelected(null); setShowDetails(false); loadConversations(false); }}>
                <ChevronLeft size={20} />
              </button>
              {/* Tapping the contact opens the details drawer (WhatsApp behaviour) */}
              <button className="wai-chat__contact-trigger" onClick={() => setShowDetails(true)} title="Contact info">
                <ChatAvatar name={selected.name} phone={selected.phone} />
                <span className="wai-chat__contact">
                  <span className="wai-chat__contact-name">{selected.name || selected.phone}</span>
                  <span className="wai-chat__contact-phone">{selected.phone}</span>
                </span>
              </button>

              {/* Desktop: inline AI + status controls. Hidden on mobile. */}
              <div className="wai-chat__header-actions wai-chat__header-actions--desktop">
                <span className={`wai-status-pill wai-status-pill--${selected.status}`}>{selected.status}</span>
                {renderConvControls("inline")}
              </div>

              {/* Always-visible quick actions: enquiry + call + contact info */}
              {selected.enquiryId && openEnquiry ? (
                <button
                  className="wai-icon-btn"
                  onClick={() => openEnquiry(selected.enquiryId)}
                  title={selected.enquiryName ? `Open enquiry: ${selected.enquiryName}` : "Open enquiry"}
                  aria-label="Open enquiry"
                >
                  <ClipboardList size={18} />
                </button>
              ) : !selected.enquiryId && convertToEnquiry ? (
                <button
                  className="wai-icon-btn"
                  onClick={() => setConvertOpen(true)}
                  title="Convert to enquiry"
                  aria-label="Convert to enquiry"
                >
                  <UserPlus size={18} />
                </button>
              ) : null}
              {selected.phone && (
                <a className="wai-icon-btn" href={`tel:${selected.phone}`} title="Call" aria-label="Call">
                  <Phone size={18} />
                </a>
              )}
              <button className="wai-icon-btn wai-details-btn" onClick={() => setShowDetails(true)} title="Contact info" aria-label="Contact info">
                <MoreVertical size={18} />
              </button>
            </div>

            {/* Messages */}
            <div className="wai-messages" ref={messagesScrollRef} onScroll={handleMessagesScroll}>
              {msgLoading ? <Loader /> : messages.length === 0 ? (
                <div className="wai-messages-empty">No messages in this conversation</div>
              ) : (
                <>
                {/* Scroll-up history: older pages load as you reach the top. */}
                {loadingOlder && (
                  <div className="wai-older-loader">
                    <Loader2 size={14} className="wai-spin" /> Loading older messages…
                  </div>
                )}
                {!loadingOlder && !hasMoreOlder && messages.length >= MESSAGE_PAGE_SIZE && (
                  <div className="wai-older-end">Start of conversation</div>
                )}
                {grouped.map((item, i) =>
                  item.type === "date" ? (
                    <div key={`d-${i}`} className="wai-date-sep">
                      <span className="wai-date-sep__label">{item.label}</span>
                    </div>
                  ) : (
                    <div
                      key={item.data._id || i}
                      ref={(el) => {
                        const id = String(item.data._id || "");
                        if (!id) return;
                        if (el) bubbleRefs.current.set(id, el);
                        else bubbleRefs.current.delete(id);
                      }}
                      className={
                        `wai-bubble-row${item.data.direction === "outbound" ? " wai-bubble-row--out" : " wai-bubble-row--in"}`
                        + (highlightedMsgId === String(item.data._id) ? " wai-bubble-row--highlight" : "")
                        + (swipeMsgId === String(item.data._id) ? " wai-bubble-row--swiping" : "")
                      }
                      style={swipeMsgId === String(item.data._id) && swipeDx
                        ? { transform: `translateX(${swipeDx}px)` }
                        : undefined}
                      {...swipeHandlersFor(item.data)}
                    >
                      {/* Reply affordance revealed underneath the bubble as it slides. */}
                      {swipeMsgId === String(item.data._id) && swipeDx > 0 && (
                        <span className="wai-swipe-hint" aria-hidden="true">
                          <CornerUpLeft size={16} />
                        </span>
                      )}
                      <div className={`wai-bubble${item.data.direction === "outbound" ? " wai-bubble--out" : " wai-bubble--in"}`}>
                        {!item.data.deleted && (canReply || item.data.content || deleteMessage) && (
                          <div className="wai-bubble__menu-wrap">
                            {/* WhatsApp-style hover chevron — opens a portal menu */}
                            <button
                              type="button"
                              className="wai-bubble__menu-btn"
                              title="Message options"
                              aria-haspopup="menu"
                              aria-expanded={menuMsg?._id === item.data._id}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (menuMsg?._id === item.data._id) setMenuMsg(null);
                                else openMsgMenu(item.data, e.currentTarget);
                              }}
                            >
                              <ChevronDown size={15} />
                            </button>
                          </div>
                        )}
                        {item.data.forwarded && !item.data.deleted && (
                          <span className="wai-bubble__forwarded">
                            <Forward size={12} /> Forwarded
                          </span>
                        )}
                        <QuotedMessage
                          message={item.data}
                          contactName={selected?.name || selected?.phone}
                          onJumpTo={handleJumpToMessage}
                        />
                        <div className="wai-bubble__body">
                          <MessageRenderer message={item.data} mediaEndpoint={mediaEndpoint} />
                        </div>
                        <span className="wai-bubble__meta">
                          {item.data.generatedBy === 'ai' && (
                            <span className="wai-bubble__ai-badge" title={`AI reply${item.data.aiMeta?.confidence ? ' (confidence ' + Math.round(item.data.aiMeta.confidence * 100) + '%)' : ''}${item.data.aiMeta?.intent ? ' · ' + item.data.aiMeta.intent : ''}`}>
                              <Bot size={11} /> AI
                              {Array.isArray(item.data.aiMeta?.knowledgeChunkIds) && item.data.aiMeta.knowledgeChunkIds.length > 0 && (
                                <button
                                  type="button"
                                  className="wai-bubble__ai-citations"
                                  title={`Grounded in ${item.data.aiMeta.knowledgeChunkIds.length} knowledge chunk${item.data.aiMeta.knowledgeChunkIds.length === 1 ? '' : 's'} — click to view`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openCitationPopover(item.data._id, item.data.aiMeta.knowledgeChunkIds);
                                  }}
                                  style={{
                                    marginLeft: 4, opacity: 0.85, background: 'transparent',
                                    border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit',
                                  }}
                                >
                                  · 📚 {item.data.aiMeta.knowledgeChunkIds.length}
                                </button>
                              )}
                            </span>
                          )}
                          {new Date(item.data.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          {item.data.direction === "outbound" && (
                            <span className="wai-bubble__status">{statusIcon(item.data.status)}</span>
                          )}
                        </span>
                      </div>
                    </div>
                  )
                )}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* AI Draft suggestions */}
            {canReply && aiDrafts.length > 0 && (
              <div className="wai-ai-drafts">
                <div className="wai-ai-drafts__header">
                  <Bot size={14} /> AI suggested {aiDrafts.length === 1 ? 'reply' : 'replies'}
                  <span className="wai-ai-drafts__hint">— review and send, edit, or discard</span>
                </div>
                {aiDrafts.map(d => {
                  const editing = aiDraftEdit[d._id] !== undefined;
                  const text = editing ? aiDraftEdit[d._id] : d.draftText;
                  const busy = aiDraftBusy[d._id];
                  return (
                    <div key={d._id} className="wai-ai-draft">
                      <textarea
                        className="wai-ai-draft__text"
                        value={text}
                        onChange={(e) => setAiDraftEdit(prev => ({ ...prev, [d._id]: e.target.value }))}
                        rows={Math.min(6, Math.max(2, Math.ceil(text.length / 60)))}
                      />
                      <div className="wai-ai-draft__actions">
                        {d.intent && <span className="wai-ai-draft__intent">{d.intent}</span>}
                        {typeof d.confidence === 'number' && (
                          <span className="wai-ai-draft__confidence">conf {Math.round(d.confidence * 100)}%</span>
                        )}
                        <div style={{ flex: 1 }} />
                        <button
                          className="wai-action-btn wai-action-btn--close"
                          onClick={() => handleDiscardAIDraft(d)}
                          disabled={!!busy}
                          title="Discard this draft"
                        >
                          {busy === 'discarding' ? <Loader2 size={13} className="wai-spin" /> : <Trash2 size={13} />} Discard
                        </button>
                        <button
                          className="wai-action-btn"
                          onClick={() => handleSendAIDraft(d)}
                          disabled={!!busy}
                        >
                          {busy === 'sending' ? <Loader2 size={13} className="wai-spin" /> : <Send size={13} />}
                          {editing && aiDraftEdit[d._id] !== d.draftText ? ' Send edited' : ' Send'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Quoted-reply preview — sits above the composer, WhatsApp-style */}
            {replyingTo && (
              <div className="wai-reply-preview">
                <div className="wai-reply-preview__bar" />
                <div className="wai-reply-preview__body">
                  <span className="wai-reply-preview__author">
                    {replyingTo.direction === "outbound"
                      ? "You"
                      : (selected?.name || selected?.phone || "Contact")}
                  </span>
                  <span className="wai-reply-preview__text">
                    {replyingTo.content || `[${replyingTo.messageType}]`}
                  </span>
                </div>
                <button
                  type="button"
                  className="wai-reply-preview__cancel"
                  onClick={handleCancelReply}
                  aria-label="Cancel reply"
                >
                  <X size={18} />
                </button>
              </div>
            )}

            {/* Reply bar */}
            {canReply && (
            <div className="wai-reply">
              {/* Pickers are multi-select: the preview tray is built to hold a
                  batch, and picking three images one dialog at a time is the
                  slowest possible way to get there. */}
              <input
                ref={mediaInputRef}
                type="file"
                accept="image/*,video/mp4,audio/*"
                multiple
                style={{ display: "none" }}
                onChange={(e) => { const f = e.target.files; handleFilesPicked(f, "media"); e.target.value = ""; }}
              />
              <input
                ref={stickerInputRef}
                type="file"
                accept="image/webp"
                style={{ display: "none" }}
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleStickerPicked(f); }}
              />
              <input
                ref={docInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
                multiple
                style={{ display: "none" }}
                onChange={(e) => { const f = e.target.files; handleFilesPicked(f, "document"); e.target.value = ""; }}
              />

              {/* One "+" instead of five icons. On a 360px phone those icons
                  left the text field barely wider than the send button. */}
              <div className="wai-attach">
                {attachMenuOpen && (
                  <>
                    <div className="wai-attach__backdrop" onClick={() => setAttachMenuOpen(false)} />
                    <div className="wai-attach__menu" role="menu">
                      <button
                        type="button"
                        className="wai-attach__item"
                        role="menuitem"
                        onClick={() => { setAttachMenuOpen(false); mediaInputRef.current?.click(); }}
                      >
                        <span className="wai-attach__icon wai-attach__icon--media"><ImageIcon size={18} /></span>
                        Photos &amp; videos
                      </button>
                      <button
                        type="button"
                        className="wai-attach__item"
                        role="menuitem"
                        onClick={() => { setAttachMenuOpen(false); docInputRef.current?.click(); }}
                      >
                        <span className="wai-attach__icon wai-attach__icon--doc"><Paperclip size={18} /></span>
                        Document
                      </button>
                      <button
                        type="button"
                        className="wai-attach__item"
                        role="menuitem"
                        onClick={() => { setAttachMenuOpen(false); stickerInputRef.current?.click(); }}
                      >
                        <span className="wai-attach__icon wai-attach__icon--sticker"><Smile size={18} /></span>
                        Sticker <span className="wai-attach__hint">.webp</span>
                      </button>
                      {templatesEnabled && (
                        <button
                          type="button"
                          className="wai-attach__item"
                          role="menuitem"
                          title="Use this to re-open an expired 24-hour window"
                          onClick={() => { setAttachMenuOpen(false); setTemplateMode("reply"); }}
                        >
                          <span className="wai-attach__icon wai-attach__icon--template"><FileText size={18} /></span>
                          Template
                        </button>
                      )}
                      {interactiveEnabled && (
                        <button
                          type="button"
                          className="wai-attach__item"
                          role="menuitem"
                          title="Each button runs a real action when tapped"
                          onClick={() => { setAttachMenuOpen(false); setShowInteractive(true); }}
                        >
                          <span className="wai-attach__icon wai-attach__icon--action"><MousePointerClick size={18} /></span>
                          Action buttons
                        </button>
                      )}
                    </div>
                  </>
                )}
                <button
                  type="button"
                  className={`wai-attach__btn${attachMenuOpen ? " wai-attach__btn--open" : ""}`}
                  title="Attach"
                  aria-label="Attach"
                  aria-haspopup="menu"
                  aria-expanded={attachMenuOpen}
                  onClick={() => setAttachMenuOpen((o) => !o)}
                  disabled={uploading || sending}
                >
                  <Plus size={22} />
                </button>
              </div>
              <AutoGrowTextarea
                className="wai-reply__input"
                inputRef={composerRef}
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                placeholder={uploading ? "Uploading attachment…" : replyingTo ? "Reply…" : "Type a message"}
                disabled={uploading}
                onKeyDown={e => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendReply(); }
                  // Escape drops the quote first; a second press is free to do
                  // whatever it normally would.
                  else if (e.key === "Escape" && replyingTo) { e.preventDefault(); handleCancelReply(); }
                }}
              />
              <button
                className="wai-reply__send"
                onClick={handleSendReply}
                disabled={sending || uploading || !replyText.trim()}
              >
                {uploading ? <Loader2 size={18} className="wai-spin" /> : <Send size={18} />}
              </button>
            </div>
            )}

            {/* ── Attachment preview tray ────────────────────────────────────
                Covers the chat pane between picking files and sending them:
                caption per file, discard the wrong one, add more. Nothing has
                been uploaded at this point — closing it costs no bandwidth. */}
            {pendingMedia && (() => {
              const items = pendingMedia.items;
              const active = items.find((it) => it.id === pendingMedia.activeId) || items[0];
              const busy = !!mediaSending;
              const isVideo = active.file.type.startsWith("video/");
              // WhatsApp carries no caption on an audio message — offering the
              // box would silently drop what was typed into it.
              const captionable = !active.file.type.startsWith("audio/");
              return (
                <div className="wai-mp" role="dialog" aria-label="Attachment preview">
                  <div className="wai-mp__bar">
                    <button
                      type="button"
                      className="wai-icon-btn"
                      onClick={discardPendingMedia}
                      disabled={busy}
                      title="Discard all"
                      aria-label="Discard all"
                    >
                      <X size={20} />
                    </button>
                    <span className="wai-mp__title">
                      {items.length === 1 ? active.file.name : `${items.length} files`}
                      <span className="wai-mp__to">To {selected.name || selected.phone}</span>
                    </span>
                    <button
                      type="button"
                      className="wai-icon-btn wai-mp__drop"
                      onClick={() => removePendingItem(active.id)}
                      disabled={busy}
                      title="Remove this file"
                      aria-label="Remove this file"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>

                  <div className="wai-mp__stage">
                    {active.url && !isVideo && (
                      <img className="wai-mp__media" src={active.url} alt={active.file.name} />
                    )}
                    {active.url && isVideo && (
                      // eslint-disable-next-line jsx-a11y/media-has-caption
                      <video className="wai-mp__media" src={active.url} controls />
                    )}
                    {!active.url && (
                      <div className="wai-mp__doc">
                        <FileText size={56} strokeWidth={1} />
                        <span className="wai-mp__doc-name">{active.file.name}</span>
                        <span className="wai-mp__doc-size">
                          {formatBytes(active.file.size)}{active.file.type ? ` · ${active.file.type}` : ""}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Caption belongs to the file on screen, exactly like the
                      WhatsApp app — switching thumbnails switches caption. */}
                  <div className="wai-mp__caption">
                    <AutoGrowTextarea
                      className="wai-reply__input"
                      value={active.caption}
                      onChange={(e) => setPendingCaption(active.id, e.target.value)}
                      placeholder={busy
                        ? `Sending ${Math.min(mediaSending.done + 1, mediaSending.total)} of ${mediaSending.total}…`
                        : captionable ? "Type a message" : "Audio messages can't carry a caption"}
                      disabled={busy || !captionable}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendPendingMedia(); }
                      }}
                    />
                    <button
                      type="button"
                      className="wai-reply__send"
                      onClick={handleSendPendingMedia}
                      disabled={busy}
                      title={`Send ${items.length === 1 ? "file" : `${items.length} files`}`}
                    >
                      {busy ? <Loader2 size={18} className="wai-spin" /> : <Send size={18} />}
                    </button>
                  </div>

                  <div className="wai-mp__strip">
                    {items.map((it) => {
                      const thumbVideo = it.file.type.startsWith("video/");
                      return (
                        <div
                          key={it.id}
                          role="button"
                          tabIndex={0}
                          className={`wai-mp__thumb${it.id === active.id ? " wai-mp__thumb--active" : ""}`}
                          title={it.file.name}
                          onClick={() => setPendingMedia((p) => (p ? { ...p, activeId: it.id } : p))}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setPendingMedia((p) => (p ? { ...p, activeId: it.id } : p));
                            }
                          }}
                        >
                          {it.url && !thumbVideo
                            ? <img src={it.url} alt="" />
                            : (
                              <span className="wai-mp__thumb-icon">
                                {thumbVideo ? <Film size={18} /> : <FileText size={18} />}
                              </span>
                            )}
                          {/* A caption already typed is easy to lose track of
                              once the thumbnail scrolls out of the active slot. */}
                          {it.caption.trim() && <span className="wai-mp__thumb-dot" aria-hidden="true" />}
                          <button
                            type="button"
                            className="wai-mp__thumb-x"
                            onClick={(e) => { e.stopPropagation(); removePendingItem(it.id); }}
                            disabled={busy}
                            aria-label={`Remove ${it.file.name}`}
                          >
                            <X size={11} />
                          </button>
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      className="wai-mp__add"
                      onClick={() => (pendingMedia.kind === "document" ? docInputRef : mediaInputRef).current?.click()}
                      disabled={busy}
                      title="Add more"
                      aria-label="Add more"
                    >
                      <Plus size={20} />
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* ── Contact details drawer (WhatsApp "contact info") ──────────── */}
            {showDetails && (
              <div className="wai-details-overlay" onClick={() => setShowDetails(false)}>
                <aside className="wai-details" onClick={(e) => e.stopPropagation()}>
                  <div className="wai-details__bar">
                    <button className="wai-icon-btn" onClick={() => setShowDetails(false)} aria-label="Close">
                      <X size={20} />
                    </button>
                    <span className="wai-details__bar-title">Contact info</span>
                  </div>

                  <div className="wai-details__profile">
                    <div className="wai-details__avatar"><ChatAvatar name={selected.name} phone={selected.phone} /></div>
                    <h3 className="wai-details__name">{selected.name || selected.phone}</h3>
                    <span className="wai-details__phone">{selected.phone}</span>
                    {selected.studentName && (
                      <span className="wai-details__student"><User size={12} /> {selected.studentName}</span>
                    )}
                    {selected.phone && (
                      <a className="wai-details__call" href={`tel:${selected.phone}`}>
                        <Phone size={16} /> Call
                      </a>
                    )}
                  </div>

                  <div className="wai-details__section">
                    <span className="wai-details__label">Conversation</span>
                    <div className="wai-details__row">
                      <span>Status</span>
                      <span className={`wai-status-pill wai-status-pill--${selected.status}`}>{selected.status}</span>
                    </div>
                  </div>

                  {(convertToEnquiry || (selected.enquiryId && openEnquiry)) && (
                    <div className="wai-details__section">
                      <span className="wai-details__label">Enquiry</span>
                      {selected.enquiryId ? (
                        <div className="wai-details__row">
                          <span>{selected.enquiryName || "Linked enquiry"}{selected.enquiryStatus ? ` · ${selected.enquiryStatus}` : ""}</span>
                          {openEnquiry && (
                            <button className="wai-action-btn" onClick={() => openEnquiry(selected.enquiryId)}>
                              <ClipboardList size={14} /> Open enquiry
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="wai-details__row">
                          <span>Not an enquiry yet</span>
                          <button className="wai-action-btn" onClick={() => setConvertOpen(true)}>
                            <UserPlus size={14} /> Convert to enquiry
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="wai-details__section">
                    <span className="wai-details__label">AI assistant &amp; actions</span>
                    <div className="wai-details__controls">
                      {renderConvControls("drawer")}
                    </div>
                  </div>

                  {/* ── Private enquiry notes (platform / SaaS-admin only) ──── */}
                  {notesEnabled && (
                    <div className="wai-details__section">
                      <span className="wai-details__label">
                        <StickyNote size={13} style={{ marginRight: 4, verticalAlign: "-2px" }} />
                        Notes
                        <span className="wai-notes__hint"> — private, only visible to you</span>
                      </span>

                      <div className="wai-notes">
                        {/* Add new note */}
                        <div className="wai-notes__add">
                          <textarea
                            className="wai-notes__input"
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            placeholder="Add an enquiry note…"
                            rows={2}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleAddNote(); }
                            }}
                          />
                          <button
                            className="wai-notes__add-btn"
                            onClick={handleAddNote}
                            disabled={savingNote || !newNote.trim()}
                            title="Add note (Ctrl/⌘+Enter)"
                          >
                            {savingNote ? <Loader2 size={14} className="wai-spin" /> : <Plus size={14} />} Add
                          </button>
                        </div>

                        {/* Notes timeline */}
                        {notesLoading ? (
                          <div className="wai-notes__loading"><Loader2 size={14} className="wai-spin" /> Loading notes…</div>
                        ) : notes.length === 0 ? (
                          <div className="wai-notes__empty">No notes yet. Track this enquiry above.</div>
                        ) : (
                          <ul className="wai-notes__list">
                            {notes.map((n) => {
                              const editing = editingNoteId === n._id;
                              const busy = noteBusyId === n._id;
                              return (
                                <li key={n._id} className="wai-note">
                                  {editing ? (
                                    <>
                                      <textarea
                                        className="wai-notes__input"
                                        value={editingNoteText}
                                        onChange={(e) => setEditingNoteText(e.target.value)}
                                        rows={2}
                                        autoFocus
                                      />
                                      <div className="wai-note__actions">
                                        <button
                                          className="wai-note__btn"
                                          onClick={() => handleSaveEditNote(n)}
                                          disabled={busy || !editingNoteText.trim()}
                                        >
                                          {busy ? <Loader2 size={12} className="wai-spin" /> : <Check size={12} />} Save
                                        </button>
                                        <button
                                          className="wai-note__btn"
                                          onClick={() => { setEditingNoteId(null); setEditingNoteText(""); }}
                                          disabled={busy}
                                        >
                                          <X size={12} /> Cancel
                                        </button>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="wai-note__body">{n.body}</div>
                                      <div className="wai-note__footer">
                                        <span className="wai-note__time">
                                          {formatNoteTime(n.createdAt)}
                                          {n.updatedAt && n.updatedAt !== n.createdAt ? " · edited" : ""}
                                          {n.createdByName ? ` · ${n.createdByName}` : ""}
                                        </span>
                                        <span className="wai-note__icons">
                                          <button
                                            className="wai-note__icon-btn"
                                            title="Edit note"
                                            onClick={() => { setEditingNoteId(n._id); setEditingNoteText(n.body); }}
                                          >
                                            <Edit2 size={13} />
                                          </button>
                                          <button
                                            className="wai-note__icon-btn wai-note__icon-btn--danger"
                                            title="Delete note"
                                            onClick={() => setConfirmDeleteNote(n)}
                                          >
                                            <Trash2 size={13} />
                                          </button>
                                        </span>
                                      </div>
                                    </>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </div>
                  )}
                </aside>
              </div>
            )}
          </>
        )}
      </div>

      {/* Per-message options menu — portal so it escapes the scroll container's
          overflow clipping and can flip above the bubble near the screen edge. */}
      {menuMsg && menuPos && createPortal(
        <div
          className="wai-msg-menu"
          role="menu"
          style={{
            right: menuPos.rightOffset,
            ...(menuPos.top != null ? { top: menuPos.top } : {}),
            ...(menuPos.bottom != null ? { bottom: menuPos.bottom } : {}),
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {canReply && (
            <button type="button" className="wai-msg-menu__item" onClick={() => handleStartReply(menuMsg)}>
              <CornerUpLeft size={14} /> Reply
            </button>
          )}
          {menuMsg.content && (
            <button type="button" className="wai-msg-menu__item" onClick={() => handleCopyMessage(menuMsg)}>
              <CopyIcon size={14} /> Copy
            </button>
          )}
          {deleteMessage && (
            <button
              type="button"
              className="wai-msg-menu__item wai-msg-menu__item--danger"
              onClick={() => { const m = menuMsg; setMenuMsg(null); setConfirmDeleteMsg(m); }}
            >
              <Trash2 size={14} /> Delete from inbox
            </button>
          )}
        </div>,
        document.body,
      )}

      {/* Delete-from-inbox confirmation (local only — does not recall from WhatsApp) */}
      <ConfirmModal
        show={!!confirmDeleteMsg}
        onHide={() => { if (!deletingMsg) setConfirmDeleteMsg(null); }}
        onConfirm={handleConfirmDelete}
        title="Delete message?"
        message="This removes the message from your inbox only. It cannot be un-sent — the recipient still sees it on WhatsApp."
        confirmText="Delete from inbox"
        variant="danger"
        loading={deletingMsg}
      />

      {/* Delete enquiry note confirmation */}
      <ConfirmModal
        show={!!confirmDeleteNote}
        onHide={() => { if (noteBusyId == null) setConfirmDeleteNote(null); }}
        onConfirm={handleConfirmDeleteNote}
        title="Delete note?"
        message="This permanently deletes this private note. This can't be undone."
        confirmText="Delete note"
        variant="danger"
        loading={noteBusyId != null && noteBusyId === confirmDeleteNote?._id}
      />

      {/* AI Insights — per-conversation analytics drill-down */}
      <AppModal
        show={!!aiInsights?.open}
        onHide={closeAIInsights}
        size="lg"
        icon={<BarChart3 size={18} />}
        title={`AI Insights — ${selected?.name || selected?.phone || ''}`}
      >
        {aiInsights?.loading && (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-secondary, #666)' }}>
            <Loader2 size={20} className="wai-spin" /> Loading…
          </div>
        )}
        {aiInsights?.error && (
          <div style={{ color: '#c0392b', padding: 12, background: 'rgba(192,57,43,0.08)', borderRadius: 4, fontSize: 13 }}>
            {aiInsights.error}
          </div>
        )}
        {!aiInsights?.loading && !aiInsights?.error && aiInsights?.data && (() => {
              const { stats, conversation: conv, recentReplies } = aiInsights.data;
              const reply2InboundRatio = stats.inboundCount > 0
                ? (stats.aiReplies / stats.inboundCount).toFixed(2) : '—';
              const avgCostPerReply = stats.aiReplies > 0
                ? (stats.totalCostUsd / stats.aiReplies) : 0;
              return (
                <>
                  {/* Top stats grid */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: 8, marginBottom: 16,
                  }}>
                    {[
                      { label: 'AI replies', value: stats.aiReplies },
                      { label: 'Followups sent', value: stats.followupReplies },
                      { label: 'Reply / inbound', value: reply2InboundRatio },
                      { label: 'Total cost', value: `$${stats.totalCostUsd.toFixed(4)}` },
                      { label: 'Avg cost / reply', value: `$${avgCostPerReply.toFixed(5)}` },
                      { label: 'Tokens (in/out)', value: `${stats.totalPromptTokens}/${stats.totalCompletionTokens}` },
                      { label: 'Avg confidence', value: stats.avgConfidence !== null ? `${Math.round(stats.avgConfidence * 100)}%` : '—' },
                      { label: 'KB citations used', value: stats.totalCitations },
                    ].map((s) => (
                      <div key={s.label} style={{
                        padding: 10, background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0',
                      }}>
                        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{s.label}</div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: '#0f172a' }}>{s.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Conversation state */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, fontSize: 12 }}>
                    {conv.aiPaused && (
                      <span style={{ padding: '3px 8px', background: '#fef3c7', color: '#92400e', borderRadius: 4 }}>
                        AI paused
                      </span>
                    )}
                    {conv.aiHandoffAt && (
                      <span style={{ padding: '3px 8px', background: '#fee2e2', color: '#991b1b', borderRadius: 4 }}>
                        Handoff: {conv.aiHandoffReason || 'unknown'} at {new Date(conv.aiHandoffAt).toLocaleString()}
                      </span>
                    )}
                    {conv.aiFollowupOptOut && (
                      <span style={{ padding: '3px 8px', background: 'hsl(var(--primary-soft))', color: 'hsl(var(--primary-soft-fg))', borderRadius: 4 }}>
                        Followup opt-out
                      </span>
                    )}
                    {Array.isArray(stats.uniqueIntents) && stats.uniqueIntents.length > 0 && (
                      <span style={{ padding: '3px 8px', background: '#ecfeff', color: '#155e75', borderRadius: 4 }}>
                        Intents: {stats.uniqueIntents.join(', ')}
                      </span>
                    )}
                  </div>

                  {/* Recent replies table */}
                  <h4 style={{ fontSize: 13, marginBottom: 8, color: '#334155' }}>
                    Recent AI replies (last {recentReplies.length})
                  </h4>
                  {recentReplies.length === 0 ? (
                    <div style={{ color: '#999', fontSize: 13, fontStyle: 'italic', padding: 12 }}>
                      No AI replies yet for this conversation.
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>
                            <th style={{ padding: '6px 8px' }}>When</th>
                            <th style={{ padding: '6px 8px' }}>Preview</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Conf.</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Cost</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right' }}>📚</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentReplies.map((r) => (
                            <tr key={r._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: '#64748b' }}>
                                {new Date(r.createdAt).toLocaleString([], {
                                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                                })}
                                {r.followup && <span title="Proactive followup" style={{ marginLeft: 4, color: 'hsl(var(--chart-1))' }}>↻</span>}
                              </td>
                              <td style={{ padding: '6px 8px', color: '#334155' }}>{r.preview}…</td>
                              <td style={{
                                padding: '6px 8px', textAlign: 'right',
                                color: r.confidence < 0.55 ? '#dc2626' : r.confidence < 0.75 ? '#d97706' : '#059669',
                              }}>
                                {r.confidence !== null ? `${Math.round(r.confidence * 100)}%` : '—'}
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>
                                ${(r.costUsd || 0).toFixed(5)}
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>
                                {r.citationCount > 0 ? r.citationCount : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              );
            })()}
      </AppModal>

      {/* Citation drill-down modal — shows KB chunks that grounded an AI reply */}
      <AppModal
        show={!!citationPopover}
        onHide={closeCitationPopover}
        size="md"
        title={`Knowledge sources used (${citationPopover?.ids?.length || 0})`}
        icon={<span style={{ fontSize: 16 }}>📚</span>}
      >
        <p style={{ fontSize: 12, color: 'var(--text-secondary, #666)', margin: '0 0 12px' }}>
          These knowledge base chunks were retrieved by RAG and included as context for this AI reply.
        </p>
        {citationPopover?.loading && (
          <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-secondary, #666)' }}>
            <Loader2 size={20} className="wai-spin" /> Loading…
          </div>
        )}
        {citationPopover?.error && (
          <div style={{ color: '#c0392b', padding: 12, background: 'rgba(192,57,43,0.08)', borderRadius: 4, fontSize: 13 }}>
            {citationPopover.error}
          </div>
        )}
        {!citationPopover?.loading && !citationPopover?.error && citationPopover?.items?.length === 0 && (
          <div style={{ color: 'var(--text-tertiary, #999)', padding: 12, fontSize: 13, fontStyle: 'italic' }}>
            The referenced knowledge chunks could not be loaded. They may have been deleted.
          </div>
        )}
        {!citationPopover?.loading && (citationPopover?.items || []).map((chunk, idx) => (
          <div
            key={chunk._id}
            style={{
              borderLeft: '3px solid #4a90e2', padding: '8px 12px', marginBottom: 10,
              background: 'var(--bg-secondary, #f8fafc)', borderRadius: '0 4px 4px 0',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
              <strong style={{ fontSize: 13, color: 'var(--text-primary, #222)' }}>
                {idx + 1}. {chunk.title || '(untitled)'}
              </strong>
              <span style={{ fontSize: 10, color: 'var(--text-tertiary, #888)', textTransform: 'uppercase' }}>
                {chunk.source || 'manual'}
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary, #444)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
              {chunk.text}
            </div>
            {Array.isArray(chunk.tags) && chunk.tags.length > 0 && (
              <div style={{ marginTop: 6 }}>
                {chunk.tags.map((t) => (
                  <span key={t} style={{
                    fontSize: 10, padding: '2px 6px', marginRight: 4, background: 'rgba(21,101,192,0.12)',
                    color: '#1565c0', borderRadius: 10,
                  }}>{t}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </AppModal>

      {/* Template send — re-open an expired 24h window (reply) or first-contact a
          new number (compose). Only mounted when the inbox enables templates. */}
      {templatesEnabled && (
        <TemplateSendModal
          show={!!templateMode}
          onHide={() => setTemplateMode(null)}
          fetchTemplates={fetchTemplates}
          showToast={showToast}
          title={templateMode === "compose" ? "Send template to new contact" : "Send template message"}
          recipientLabel={
            templateMode === "compose"
              ? (composeRecipient?.name || composePhone || "new contact")
              : (selected?.name || selected?.phone || "")
          }
          /* Student/course/batch/fee fields only resolve when the recipient is a
             known student — a composed student, or a conversation the backend
             already linked to one. A bare number gets the contact catalog, which
             is the same split the backend enforces on the way in. */
          audienceKind={
            (templateMode === "compose" ? composeRecipient?.studentId : selected?.studentId)
              ? "student"
              : "contact"
          }
          allowDynamic={templateVarsEnabled}
          onSend={templateMode === "compose" ? handleComposeTemplateSend : handleReplyTemplateSend}
        />
      )}

      {/* Action buttons — interactive message whose buttons carry capabilities.
          Requires an open 24h window, which the backend enforces. */}
      {interactiveEnabled && selected && (
        <InteractiveComposerModal
          show={showInteractive}
          onHide={() => setShowInteractive(false)}
          fetchActions={fetchButtonActions}
          contactName={selected.name || selected.phone}
          showToast={showToast}
          onSend={async (payload) => {
            await sendInteractiveButtons(selected, payload);
            await refreshLatestMessages({ scrollToBottom: true });
            loadConversations(true);
          }}
        />
      )}

      {/* Convert to enquiry — on the chat's own number (institute inbox only). */}
      {convertToEnquiry && selected && (
        <ConvertToEnquiryModal
          show={convertOpen}
          conversation={selected}
          loadCourses={loadEnquiryCourses}
          onConvert={(body) => convertToEnquiry(selected, body)}
          onDone={handleEnquiryLinked}
          onHide={() => setConvertOpen(false)}
        />
      )}
    </div>
  );
};

export default WhatsAppChatInbox;
